import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import BankCombobox from '../components/BankCombobox';
import AccountCombobox from '../components/AccountCombobox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { parseMoneyAr } from '../lib/money';

/**
 * Alta de cheque como página (Finanzas → Cheques → Nuevo cheque).
 * Si se vincula a una cuenta corriente, el cheque se registra como pago
 * (baja la deuda del cliente o la tuya con el proveedor); si después
 * rebota, la deuda se vuelve a cargar sola.
 */

const STATUS_OPTIONS = [
  { id: 'pending', label: 'En cartera' },
  { id: 'deposited', label: 'Depositado' },
  { id: 'cleared', label: 'Acreditado' },
  { id: 'rejected', label: 'Rechazado' },
  { id: 'endorsed', label: 'Endosado' },
];

export default function CheckNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams<{ id: string }>();
  const initialType = new URLSearchParams(location.search).get('type') === 'issued' ? 'issued' : 'received';

  const [type, setType] = useState<'received' | 'issued'>(initialType);
  const [status, setStatus] = useState('pending');
  const [issueDate, setIssueDate] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [bank, setBank] = useState('');
  const [amount, setAmount] = useState('');
  const [emitterName, setEmitterName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [originType, setOriginType] = useState('');
  const [originNumber, setOriginNumber] = useState('');
  const [accounts, setAccounts] = useState<Array<{ id: string; entityId: string; displayName: string; entityType: string; currentBalance: number }>>([]);
  const [entities, setEntities] = useState<Array<{ id: string; displayName: string; entityType: string }>>([]);
  const [accountId, setAccountId] = useState('');
  const [applyToAccount, setApplyToAccount] = useState(true);
  const [freeName, setFreeName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(Boolean(editId));

  // Modo edición: cargar el cheque y precargar el formulario
  useEffect(() => {
    if (!editId) return;
    axios.get(`/api/checks/${editId}`)
      .then((r) => {
        const c = r.data?.check;
        if (!c) throw new Error('not-found');
        setType(c.type === 'issued' ? 'issued' : 'received');
        setStatus(c.status || 'pending');
        setIssueDate(c.issueDate || '');
        setReceivedDate(c.receivedDate || '');
        setDeliveryDate(c.deliveryDate || '');
        setDueDate(c.dueDate || '');
        setCheckNumber(c.checkNumber || '');
        setBank(c.bank || '');
        setAmount(c.amount != null ? String(c.amount).replace('.', ',') : '');
        setEmitterName(c.emitterName || '');
        setContactName(c.contactName || '');
        setContactPhone(c.contactPhone || '');
        setNotes(c.notes || '');
        setOriginType(c.originType || '');
        setOriginNumber(c.originNumber || '');
        setFreeName(c.type === 'issued' ? (c.providerName || '') : (c.clientName || ''));
      })
      .catch(() => {
        toast.error('No encontramos el cheque');
        navigate('/cash-register/checks');
      })
      .finally(() => setLoadingCheck(false));
  }, [editId, navigate]);

  useEffect(() => {
    if (editId) return; // editando no se re-vincula la cuenta
    axios.get('/api/current-accounts', { params: { entityType: 'all' } })
      .then((r) => {
        const list = Array.isArray(r.data?.accounts) ? r.data.accounts : [];
        setAccounts(list.map((a: any) => ({
          id: a.id,
          entityId: a.entityId || a.clientId || '',
          displayName: a.displayName || a.clientName || 'Cuenta',
          entityType: a.entityType || 'client',
          currentBalance: Number(a.currentBalance) || 0
        })));
      })
      .catch(() => {});
    // Clientes y proveedores sin cta cte también se pueden elegir (la cuenta se crea sola)
    Promise.all([
      axios.get('/api/clients?limit=10000').catch(() => ({ data: {} })),
      axios.get('/api/providers?limit=10000').catch(() => ({ data: {} }))
    ]).then(([cr, pr]) => {
      const clients = (cr.data?.clients || []).map((c: any) => ({
        id: c.id,
        displayName: c.companyName?.trim() || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Cliente',
        entityType: 'client'
      }));
      const providers = (pr.data?.providers || []).map((p: any) => ({
        id: p.id,
        displayName: p.fantasyName?.trim() || p.name?.trim() || p.companyName?.trim() || 'Proveedor',
        entityType: 'provider'
      }));
      setEntities([...clients, ...providers]);
    });
  }, [editId]);

  const accountOptions = useMemo(() => {
    const entityType = type === 'received' ? 'client' : 'provider';
    const withAccount = accounts.filter((a) => a.entityType === entityType);
    const covered = new Set(withAccount.map((a) => a.entityId));
    const withoutAccount = entities
      .filter((e) => e.entityType === entityType && !covered.has(e.id))
      .map((e) => ({ id: `entity:${e.entityType}:${e.id}`, entityId: e.id, displayName: e.displayName, entityType: e.entityType, currentBalance: null as number | null }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
    return [...withAccount, ...withoutAccount];
  }, [accounts, entities, type]);
  const selectedAccount = accountOptions.find((a) => a.id === accountId) || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dueDate) { toast.error('La fecha de cobro es obligatoria'); return; }
    const amt = parseMoneyAr(amount);
    if (Number.isNaN(amt) || amt <= 0) { toast.error('El monto debe ser mayor a 0'); return; }

    try {
      setSubmitting(true);
      const payload: any = {
        type, status, issueDate, dueDate,
        receivedDate: type === 'received' ? receivedDate : '',
        deliveryDate: type === 'issued' ? deliveryDate : '',
        checkNumber, bank, amount: amt,
        emitterName: type === 'received' ? (emitterName || freeName) : emitterName,
        clientName: type === 'received' ? (selectedAccount?.displayName || freeName) : '',
        providerName: type === 'issued' ? (selectedAccount?.displayName || freeName) : '',
        contactName, contactPhone, notes, originType, originNumber,
        ...(!editId && accountId
          ? accountId.startsWith('entity:')
            ? { entityId: accountId.split(':')[2], entityType: accountId.split(':')[1], applyToAccount }
            : { accountId, applyToAccount }
          : {})
      };
      if (editId) {
        await axios.put(`/api/checks/${editId}`, payload);
        toast.success('Cheque actualizado');
      } else {
        const res = await axios.post('/api/checks', payload);
        if (res.data?.accountPayment?.applied) {
          toast.success('Cheque cargado y pago registrado en la cuenta corriente');
        } else {
          toast.success('Cheque cargado');
        }
      }
      navigate('/cash-register/checks');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No pudimos guardar el cheque');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 text-foreground">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{editId ? 'Editar cheque' : 'Nuevo cheque'}</h1>
            <p className="text-sm text-muted-foreground">
              {editId
                ? `Cheque N° ${checkNumber || 's/n'}`
                : type === 'received' ? 'Un cheque que te dieron (entra a cartera).' : 'Un cheque que entregaste (a cubrir en su vencimiento).'}
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => navigate('/cash-register/checks')}>
            Volver
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={type} onValueChange={(v) => { setType(v as any); setAccountId(''); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">Recibido (me lo dieron)</SelectItem>
                  <SelectItem value="issued">Emitido (lo entregué)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {editId ? (
            <div className="space-y-2">
              <Label>{type === 'received' ? 'Cliente' : 'Proveedor'}</Label>
              <Input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Nombre de la contraparte" />
            </div>
          ) : (
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="space-y-2">
                  <Label>{type === 'received' ? 'Cuenta corriente del cliente (opcional)' : 'Cuenta corriente del proveedor (opcional)'}</Label>
                  <AccountCombobox
                    accounts={accountOptions}
                    value={accountId}
                    onChange={setAccountId}
                    balanceLabel={type === 'received' ? 'debe' : 'le debés'}
                  />
                </div>
                {accountId ? (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={applyToAccount} onChange={(e) => setApplyToAccount(e.target.checked)} className="h-4 w-4" />
                    Registrar como pago en la cuenta ({type === 'received' ? 'baja la deuda del cliente' : 'baja tu deuda con el proveedor'})
                  </label>
                ) : (
                  <div className="space-y-2">
                    <Label>{type === 'received' ? 'Cliente (texto libre)' : 'Proveedor (texto libre)'}</Label>
                    <Input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Nombre de la contraparte" />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Si el cheque después rebota, la deuda se vuelve a cargar sola en la cuenta vinculada.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Fecha de emisión</Label>
              <DatePicker value={issueDate} onChange={setIssueDate} />
            </div>
            {type === 'received' ? (
              <div className="space-y-2">
                <Label>Fecha de recepción</Label>
                <DatePicker value={receivedDate} onChange={setReceivedDate} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Fecha de entrega</Label>
                <DatePicker value={deliveryDate} onChange={setDeliveryDate} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Fecha de cobro <span className="text-destructive">*</span></Label>
              <DatePicker value={dueDate} onChange={setDueDate} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Número de cheque</Label>
              <Input value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} placeholder="12345678" />
            </div>
            <div className="space-y-2">
              <Label>Banco</Label>
              <BankCombobox value={bank} onChange={setBank} />
            </div>
            <div className="space-y-2">
              <Label>Monto <span className="text-destructive">*</span></Label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="150000" inputMode="decimal" required />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Origen</Label>
              <Select value={originType || 'none'} onValueChange={(v) => setOriginType(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Sin origen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin origen</SelectItem>
                  <SelectItem value="remito">Remito</SelectItem>
                  <SelectItem value="factura">Factura</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Número de origen</Label>
              <Input value={originNumber} onChange={(e) => setOriginNumber(e.target.value)} placeholder="R2026-000123" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Emisor del cheque</Label>
              <Input value={emitterName} onChange={(e) => setEmitterName(e.target.value)} placeholder="Quién firmó el cheque" />
            </div>
            <div className="space-y-2">
              <Label>Contacto</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Teléfono / Email</Label>
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones (opcional)..." />
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => navigate('/cash-register/checks')}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || loadingCheck}>
              {submitting ? 'Guardando...' : editId ? 'Guardar cambios' : 'Cargar cheque'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
